<template>
  <!-- Sub-bar: only shown on authenticated screens -->
  <div
    class="sticky top-14 z-40 flex items-center gap-2 px-4 h-9 text-xs md:pl-64"
    style="background:rgba(9,13,18,0.96); backdrop-filter:blur(6px); border-bottom:1px solid rgba(255,255,255,0.06);"
  >
    <!-- Context chip -->
    <div class="flex items-center gap-1">
      <button
        @click="open = true"
        class="flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-colors"
        :class="store.currentWarehouse
          ? 'bg-green-900/40 border border-green-700/30 text-green-300'
          : store.currentLocation
            ? 'bg-blue-900/40 border border-blue-700/30 text-blue-300'
            : 'bg-white/5 border border-white/10 text-gray-400 hover:text-gray-200'"
      >
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M21 10c0 6-9 13-9 13S3 16 3 10a9 9 0 0 1 18 0Z"/><circle cx="12" cy="10" r="3"/></svg>
        <span class="max-w-[160px] truncate">
          <template v-if="store.currentWarehouse">
            {{ store.currentLocation?.name }} · {{ store.currentWarehouse.name }}
          </template>
          <template v-else-if="store.currentLocation">
            {{ store.currentLocation.name }} · {{ $t('contextBar.allWarehouses') }}
          </template>
          <template v-else>{{ $t('contextBar.setLocation') }}</template>
        </span>
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M6 9l6 6 6-6"/></svg>
      </button>
      <button
        v-if="store.currentLocation"
        @click="store.setCurrentLocation(null)"
        class="flex items-center justify-center w-5 h-5 rounded-full bg-white/8 hover:bg-white/15 text-gray-400 hover:text-white transition-colors"
        :title="$t('contextBar.clearFilter')"
      >
        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M18 6 6 18M6 6l12 12"/></svg>
      </button>
    </div>

    <!-- Slot for page-specific actions (e.g. filter chips from a view) -->
    <slot />
  </div>

  <!-- Picker bottom-sheet ───────────────────────────────────────────────── -->
  <Transition name="slide-up">
    <div
      v-if="open"
      class="fixed bottom-0 left-0 right-0 z-[60] rounded-t-2xl overflow-hidden shadow-2xl"
      style="background:#0f1720; border-top:1px solid rgba(255,255,255,0.10); max-height:85vh; display:flex; flex-direction:column;"
    >
      <!-- Handle -->
      <div class="pt-3 pb-1 flex justify-center shrink-0">
        <div class="w-9 h-1 rounded-full bg-white/20" />
      </div>

      <!-- Header -->
      <div class="px-5 pb-3 shrink-0">
        <h2 class="text-base font-semibold text-white">
          {{ pickerStep === 'location' ? $t('contextBar.selectLocation') : $t('contextBar.selectWarehouse', { name: pendingLocation?.name }) }}
        </h2>
        <p class="text-xs text-gray-400 mt-0.5">
          {{ pickerStep === 'location' ? $t('contextBar.locationDesc') : $t('contextBar.warehouseDesc') }}
        </p>
      </div>

      <!-- Back arrow when on warehouse step -->
      <button
        v-if="pickerStep === 'warehouse'"
        @click="pickerStep = 'location'; pendingLocation = null"
        class="mx-5 mb-3 flex items-center gap-1.5 text-xs text-gray-400 hover:text-white transition-colors shrink-0"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
        {{ $t('contextBar.backToLocations') }}
      </button>

      <!-- List area -->
      <div class="flex-1 overflow-y-auto px-4 pb-6" style="overscroll-behavior:contain;">

        <!-- Loading -->
        <div v-if="loading || store.isLoadingLocations" class="flex items-center justify-center py-10 text-gray-400 text-sm">{{ $t('common.loading') }}</div>

        <!-- STEP: Location -->
        <template v-else-if="pickerStep === 'location'">
          <div v-if="!locations.length && !loading && !store.isLoadingLocations" class="text-center py-10 text-gray-500 text-sm">{{ $t('contextBar.noLocations') }}</div>
          <button
            v-for="loc in locations"
            :key="loc.id"
            @click="selectLocation(loc)"
            class="w-full flex items-center gap-3 p-3.5 rounded-xl mb-2 text-left transition-colors"
            :class="store.currentLocation?.id === loc.id
              ? 'bg-green-900/40 border border-green-700/30'
              : 'bg-white/4 border border-white/6 hover:bg-white/8'"
          >
            <div class="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0 text-sm">🏢</div>
            <div>
              <div class="text-sm font-medium text-white">{{ loc.name }}</div>
              <div v-if="loc.address" class="text-xs text-gray-400 mt-0.5">{{ loc.address }}</div>
            </div>
            <svg class="ml-auto text-gray-500 shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 18 6-6-6-6"/></svg>
          </button>
        </template>

        <!-- STEP: Warehouse -->
        <template v-else-if="pickerStep === 'warehouse'">
          <!-- "No filter" option -->
          <button
            @click="confirmWarehouse(null)"
            class="w-full flex items-center gap-3 p-3.5 rounded-xl mb-2 text-left transition-colors bg-white/4 border border-white/6 hover:bg-white/8"
          >
            <div class="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0 text-lg">🌐</div>
            <div class="text-sm font-medium text-white">{{ $t('contextBar.allWarehouses') }}</div>
          </button>
          <div v-if="!warehouses.length && !loading" class="text-center py-6 text-gray-500 text-sm">{{ $t('contextBar.noWarehouses') }}</div>
          <button
            v-for="wh in warehouses"
            :key="wh.id"
            @click="confirmWarehouse(wh)"
            class="w-full flex items-center gap-3 p-3.5 rounded-xl mb-2 text-left transition-colors"
            :class="store.currentWarehouse?.id === wh.id
              ? 'bg-green-900/40 border border-green-700/30'
              : 'bg-white/4 border border-white/6 hover:bg-white/8'"
          >
            <div class="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0 text-sm">🏭</div>
            <div class="text-sm font-medium text-white">{{ wh.name }}</div>
            <svg v-if="store.currentWarehouse?.id === wh.id" class="ml-auto text-green-400 shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m20 6-11 11-5-5"/></svg>
          </button>
        </template>

      </div>
    </div>
  </Transition>
  <div v-if="open" class="fixed inset-0 bg-black/50" style="z-index:59" @click="open = false" />
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from '@/stores/store.js';
import { getWarehouse } from '@/api/api.js';

const store = useStore();
const { t } = useI18n();

const open            = ref(false);
const pickerStep      = ref('location'); // 'location' | 'warehouse'
const pendingLocation = ref(null);
const warehouses      = ref([]);
const loading         = ref(false);

// Use already-loaded locations from the store; fetch if still empty
const locations = computed(() => store.locations);

watch(open, async (val) => {
  if (!val) { pickerStep.value = 'location'; pendingLocation.value = null; return; }
  // ensure locations are loaded
  if (!store.locations.length && !store.isLoadingLocations) {
    await store.fetchLocations();
  }
});

const selectLocation = async (loc) => {
  pendingLocation.value = loc;
  pickerStep.value = 'warehouse';
  loading.value = true;
  try {
    const res = await getWarehouse(0, loc.id);
    warehouses.value = res.results || [];
  } catch { warehouses.value = []; }
  finally { loading.value = false; }
};

const confirmWarehouse = (wh) => {
  if (pendingLocation.value?.id !== store.currentLocation?.id) {
    store.setCurrentLocation(pendingLocation.value);
  }
  store.setCurrentWarehouse(wh);
  open.value = false;
};
</script>

<style scoped>
.slide-up-enter-active, .slide-up-leave-active { transition: transform .22s ease, opacity .22s ease; }
.slide-up-enter-from, .slide-up-leave-to       { transform: translateY(100%); opacity: 0; }
/* z-49 workaround — Tailwind doesn't have it by default */
.z-49 { z-index: 49; }
</style>
